import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/** How close to the window's top a pointer brings the head down; a finger gets more room. */
const EDGE = 8;
const TOUCH_EDGE = 24;

/**
 * The head over a page that owns the whole window: hidden, and laid over the
 * page while the pointer is at the window's top edge or on the head itself.
 * Nothing is drawn at the edge to catch it, so the page keeps every press
 * there. A touch at the edge shows it and a touch elsewhere on the page hides
 * it again. A new page, or one that stops owning the window, starts hidden.
 */
export function useHeadPeek(peekable: boolean, at: string, head: RefObject<HTMLElement | null>) {
  const [peek, setPeek] = useState(false);
  useEffect(() => setPeek(false), [peekable, at]);

  useEffect(() => {
    if (!peekable) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' && e.clientY <= EDGE) setPeek(true);
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && head.current?.contains(e.target)) return;
      if (e.pointerType === 'touch' && e.clientY <= TOUCH_EDGE) setPeek(true);
      else setPeek(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [peekable, head]);

  return {
    shown: peekable && peek,
    hide: () => setPeek(false),
  };
}
